const PREFIX = "totonoeyo.activity.v1.";
const MAX_AGE_MS = 30 * 86400000;
const MAX_PENDING = 200;

export function createGameActivityTracker({ storage, fetchImpl = fetch, now = Date.now } = {}) {
  const memory = new Map();
  let flushing = null;
  let revision = 0;

  function pending() {
    const entries = new Map(memory);
    try {
      for (let i = 0; i < storage.length; i += 1) {
        const key = storage.key(i);
        if (key?.startsWith(PREFIX)) {
          try { entries.set(key, JSON.parse(storage.getItem(key))); }
          catch { entries.set(key, null); }
        }
      }
    } catch { /* Storage restrictions must not interrupt play. */ }
    for (const [key, payload] of entries) {
      if (!payload || typeof payload.occurredAt !== "string" || !Number.isFinite(Date.parse(payload.occurredAt))) {
        remove(key);
        entries.delete(key);
      }
    }
    return [...entries].sort((a, b) => a[1].occurredAt.localeCompare(b[1].occurredAt));
  }

  function remove(key) {
    memory.delete(key);
    try { storage.removeItem(key); } catch { /* Best effort if storage is unavailable. */ }
  }

  function record(event, game) {
    revision += 1;
    const payload = {
      attemptId: game.attemptId, event, date: game.date, locale: game.locale,
      difficulty: game.difficultyKey, occurredAt: new Date(now()).toISOString()
    };
    if (event === "ended" && Array.isArray(game.slots)) {
      payload.result = {
        puzzleVersion: game.puzzleVersion,
        finalRows: game.slots.map(row => row.map(tile => tile.letter)),
        rowSwapsUsed: ({ easy: 3, medium: 2, hard: 1 }[game.difficultyKey]) - game.movesRemaining
      };
    }
    const key = `${PREFIX}${payload.attemptId}.${event}`;
    memory.set(key, payload);
    try {
      const existing = storage.getItem(key);
      if (existing) memory.set(key, JSON.parse(existing));
      else storage.setItem(key, JSON.stringify(payload));
    } catch { /* Keep the event in memory when storage is unavailable. */ }
    for (const [oldKey] of pending().slice(0, -MAX_PENDING)) remove(oldKey);
  }

  function flush() {
    if (flushing) return flushing;
    const startRevision = revision;
    flushing = (async () => {
      for (const [key, payload] of pending()) {
        if (now() - Date.parse(payload.occurredAt) > MAX_AGE_MS) {
          remove(key);
          continue;
        }
        try {
          const response = await fetchImpl("/api/game-activity", {
            method: "POST", headers: { "content-type": "application/json" },
            body: JSON.stringify(payload), keepalive: true, credentials: "omit",
            signal: AbortSignal.timeout(5000)
          });
          if (response.ok || [400, 403, 404, 409, 413].includes(response.status)) remove(key);
          else break;
        } catch { break; }
      }
    })().finally(() => {
      flushing = null;
      if (revision !== startRevision) void flush();
    });
    return flushing;
  }
  return { record, flush };
}
